import AbstractService from '../../../abstract-service'
import ProvianteClient from 'api/proviante/proviante-client'
import UsersResponse from 'modules/user/types/response/users-response'
import { AvailableStatusData } from 'modules/user/types/response/available-status-response'
import PaginationRequest from 'service/request/pagination'
import UserResponse from 'modules/user/types/response/user-response'
import User from 'modules/user/types/user'

/**
 * Тело запроса/ответа редактирования пользователя. Полного контракта у эндпоинтов
 * нет, поэтому описаны только поля, которые реально читает UserEditor.
 */
export type EditableUserPayload = {
  roles?: string[]
  storeCodes?: string[]
  retailCodes?: string[]
  [key: string]: unknown
}

export type EditableUserResponse = {
  user: EditableUserPayload
}

class UserService extends AbstractService {
  constructor(private client: ProvianteClient) {
    super()
  }

  public async fetchEditable(id: number): Promise<EditableUserResponse> {
    const response = await this.client.post<EditableUserResponse>('/api/v1/external/user/fetch', {
      id,
    })
    return response.data
  }

  public async updateEditable(data: EditableUserPayload): Promise<void> {
    await this.client.post('/api/v1/external/user/update', data)
  }

  public async fetch(
    pagination: PaginationRequest | null = null,
    term: string | null = null,
  ): Promise<UsersResponse> {
    const data: {
      pagination?: { page: number; size: number }
      term: string
    } = {
      term: term ?? '',
    }

    if (
      pagination !== null &&
      Number.isFinite(pagination.page) &&
      Number.isFinite(pagination.size)
    ) {
      data.pagination = { page: pagination.page, size: pagination.size }
    }

    const users = await this.client.post<UsersResponse>('/api/v1/external/users', data)
    return users.data
  }

  public async changePassword(username: string, password: string): Promise<void> {
    const data = {
      username,
      password,
    }
    await this.client.post('/api/v1/external/user/password/upgrade', data)
  }

  public async getAvailableStatus(username: string): Promise<AvailableStatusData> {
    const encodedUsername = encodeURIComponent(username)
    const response = await this.client.get<AvailableStatusData>(
      `/api/v1/external/user/get-available-status?username=${encodedUsername}`,
    )
    return response.data
  }

  public async transitionStatus(username: string, transition: string): Promise<void> {
    const data = {
      username,
      transition,
    }
    await this.client.post('/api/v1/external/user/transition-status', data)
  }

  public async viewCurrentUser(): Promise<User> {
    const response = await this.client.get<UserResponse>('/api/v1/external/view-current-user')
    return response.data.user
  }
}

const userService = new UserService(ProvianteClient.getInstance())
export default userService
